import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X, LogIn, LayoutDashboard } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import SocialLinks from "@/components/SocialLinks";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/floor-plans", label: "Floor Plans" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);

  const dashboardLink = user && user.role === "admin" ? "/admin" : user && user.role === "client" ? "/portal" : null;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-link">
            <img src="/brand/logo-white-512.png" alt="Coastal Bend Contractor Co." className="h-16 lg:h-20 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g,'-')}`}
                className="text-[14px] font-medium transition-colors"
                style={({ isActive }) => isActive ? { color: 'var(--accent)' } : { color: 'var(--text-muted)' }}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {dashboardLink ? (
              <Link to={dashboardLink} className="btn-ghost !py-2 !px-4 !text-sm" data-testid="header-dashboard-btn">
                <LayoutDashboard size={15} /> Dashboard
              </Link>
            ) : (
              <Link to="/login" className="btn-ghost !py-2 !px-4 !text-sm" data-testid="header-login-btn">
                <LogIn size={15} /> Sign In
              </Link>
            )}
            <Link to="/contact" className="btn-primary !py-2 !px-4 !text-sm" data-testid="header-quote-btn">Get a Quote</Link>
          </div>

          <button className="lg:hidden text-white" onClick={() => setOpen((s) => !s)} data-testid="mobile-menu-btn">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden border-t border-white/5 px-5 py-4 space-y-2" style={{background: 'rgba(11,19,43,0.95)'}}>
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className="block py-2 text-[15px]" data-testid={`mnav-${n.label.toLowerCase()}`}>{n.label}</Link>
            ))}
            <div className="pt-3 flex gap-3">
              {dashboardLink ? (
                <Link to={dashboardLink} className="btn-ghost !py-2 !px-4 !text-sm flex-1 justify-center">Dashboard</Link>
              ) : (
                <Link to="/login" className="btn-ghost !py-2 !px-4 !text-sm flex-1 justify-center">Sign In</Link>
              )}
              <Link to="/contact" className="btn-primary !py-2 !px-4 !text-sm flex-1 justify-center">Quote</Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-white/5 mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <Link to="/" className="inline-block mb-5">
              <img src="/brand/logo-white-512.png" alt="Coastal Bend Contractor Co." className="h-24 w-auto object-contain" />
            </Link>
            <p className="text-sm max-w-md leading-relaxed mb-6" style={{color:'var(--text-muted)'}}>
              Custom new construction, remodels, and renovations across Corpus Christi and Nueces County. Built for the Texas Gulf Coast — built to last.
            </p>
            <SocialLinks />
          </div>
          <div>
            <div className="label-overline mb-4">Visit</div>
            <ul className="space-y-2 text-sm" style={{color:'var(--text-muted)'}}>
              {NAV.map((n) => (<li key={n.to}><Link to={n.to} className="hover:text-white">{n.label}</Link></li>))}
            </ul>
          </div>
          <div>
            <div className="label-overline mb-4">Contact</div>
            <ul className="space-y-3 text-sm" style={{color:'var(--text-muted)'}}>
              <li className="flex gap-2"><Phone size={15} className="mt-0.5" /> (361) 833-1309</li>
              <li className="flex gap-2"><Mail size={15} className="mt-0.5" /> info@coastalbendcontractor.com</li>
              <li className="flex gap-2"><MapPin size={15} className="mt-0.5" /> Corpus Christi, TX · Nueces County</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5 py-5 text-xs text-center" style={{color:'var(--text-dim)'}}>
          © {new Date().getFullYear()} Coastal Bend Contractor Co. · Licensed &amp; insured · Texas Gulf Coast
        </div>
      </footer>
    </div>
  );
}
